// The DOM FX layer (#fx, z5; IMPLEMENTATION §2.6 layer model, §3 M10; UX §0 "Why z5 exists", §5): cosmetic
// SimEvents → coin flights to the HUD (a sale, a refund, ghost change, a Gallery shot), coins flying OUT of
// the HUD onto a bought building, "+$X" floaters, NEW! ribbons for new tiers and the stat label of a
// building merge. The pure model (fx/domFxModel.ts) holds every record and the caps; this module maps events
// to it and copies it onto pooled nodes (transform + opacity only, will-change: transform) on
// requestAnimationFrame — the loop runs only while something is alive. Targets resolve through
// fx/targets.ts (L16: M7 registers the HUD cash, the counter …); a missing target falls back to the layout
// (the HUD's top-left area, the counter band). Reduce motion (UX §5): no coin flights (the HUD ticks), one
// floater per source, Gallery floaters every 2 s, ribbons fade without rising. Zero-duration mode (?test
// without ?fx=1): nothing is shown at all. A landed HUD coin dispatches `gm:fx-arrive` ({target, n}) on the
// window, so M7's HUD can pop (juice.json sell.hudPopScale / hudPopMs). Time is an FX clock (the board's
// kind, render/juice/clock.ts): `__gm.render.freezeAt` freezes the DOM FX together with the board.
import { GALLERY } from '../art/anchors.ts';
import { mulberry32 } from '../art/rng.ts';
import type { JuiceData } from '../data/schema.ts';
import { cellCenter, type Layout } from '../layout/computeLayout.ts';
import { FxClock } from '../render/juice/clock.ts';
import type { ShotGate } from '../render/juice/shotGate.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { SimEvent } from '../sim/events.ts';
import type { SimState } from '../sim/state.ts';
import {
  coinAt,
  coinCount,
  type DomFxCfg,
  DomFxModel,
  type DomFxStats,
  floaterPx,
  type Pt,
} from './domFxModel.ts';
import { fxTarget } from './targets.ts';

export interface DomFxCopy {
  /** "+{value}" (copy.json drag.sell). */
  floater: string;
  /** "NEW!" (copy.json tag.new). */
  newTag: string;
  /** "{name} L{level}" (copy.json drag.bmerge without the arrow). */
  level: string;
}

export interface DomFxColors {
  text: string;
  ink: string;
  ribbon: string;
  label: string;
  coin: string;
}

export interface DomFxOptions {
  root: HTMLElement;
  juice: Pick<JuiceData, 'caps' | 'aggregation' | 'reduceMotion' | 'events' | 'board'>;
  colors: DomFxColors;
  copy: DomFxCopy;
  /** Zero-duration mode: no DOM FX at all (?test unless ?fx=1). */
  instant: boolean;
  layout(): Layout | null;
  state(): SimState;
  defs(): SimDefs;
  /** Total income per second (60 s window): coin counts and floater sizes scale against a minute of it. */
  incomePerSec(): number;
  money(value: number): string;
  fill(template: string, vars: Record<string, string | number>): string;
  /** A coin icon as a CSS url (drawn once by art/domIcons), or null (a CSS coin is drawn instead). */
  coinImage(px: number): string | null;
  reduceMotion(): boolean;
  now(): number;
  raf(cb: (t: number) => void): number;
  cancelRaf(id: number): void;
  /** The arc spread's random source; default a seeded mulberry32 (deterministic flights). */
  rand?: () => number;
  /** The FX clock's largest step (ui.json render.frameClampMs). */
  maxStepMs: number;
  /** The visible-shot decision shared with the board FX (one coin per VISIBLE shot, UX §5). */
  shots: ShotGate;
}

/** `__gm.render.fx().dom`: the model's counters, the shown nodes and the FX clock. */
export interface DomFxRunStats extends DomFxStats {
  nodes: number;
  clockMs: number;
  frozen: boolean;
}

export interface DomFx {
  onEvents(evs: readonly SimEvent[]): void;
  /** A load / silent rebuild: every flight and floater goes. */
  reset(): void;
  stats(): DomFxRunStats;
  resetPeaks(): void;
  busy(): boolean;
  /** Freezes the DOM FX `ms` after the next frame (null resumes): `__gm.render.freezeAt`. */
  freezeAt(ms: number | null): void;
}

const STYLE_ID = 'gm-fx-style';
const DOM_FX_SEED = 0x20;

function installStyle(doc: Document, c: DomFxColors): void {
  if (doc.getElementById(STYLE_ID)) return;
  const st = doc.createElement('style');
  st.id = STYLE_ID;
  // an outlined, heavy rounded face reads on every background (no bitmap font, UX §5 caps table)
  const outline = `-1px -1px 0 ${c.ink},1px -1px 0 ${c.ink},-1px 1px 0 ${c.ink},1px 1px 0 ${c.ink},0 2px 3px rgba(0,0,0,.45)`;
  st.textContent =
    `.gm-fx{position:absolute;left:0;top:0;pointer-events:none;will-change:transform,opacity;` +
    `font-family:ui-rounded,system-ui,sans-serif;white-space:nowrap;display:none}` +
    `.gm-fx-coin{border-radius:50%;background-size:100% 100%;` +
    `filter:drop-shadow(0 2px 2px rgba(0,0,0,.35))}` +
    `.gm-fx-coin.css{background:radial-gradient(circle at 35% 35%,#fff6 0 18%,transparent 19%),${c.coin};` +
    `box-shadow:inset 0 0 0 2px ${c.ink}}` +
    `.gm-fx-float{font-weight:800;color:${c.text};text-shadow:${outline};` +
    `transform-origin:50% 100%;font-variant-numeric:tabular-nums}` +
    `.gm-fx-ribbon{font-weight:800;color:${c.ink};padding:2px 10px;border-radius:999px;` +
    `border:2px solid ${c.ink};box-shadow:0 3px 0 ${c.ink}}` +
    `.gm-fx-ribbon.new{background:${c.ribbon}}.gm-fx-ribbon.label{background:${c.label}}` +
    `.gm-fx-ribbon b{margin-right:6px}`;
  doc.head.appendChild(st);
}

/** A pool of `.gm-fx` nodes of one kind: `take(i)` returns node i (created on demand), the rest hide. */
class NodePool {
  private readonly nodes: HTMLElement[] = [];
  private used = 0;
  private readonly root: HTMLElement;
  private readonly cls: string;

  constructor(root: HTMLElement, cls: string) {
    this.root = root;
    this.cls = cls;
  }

  begin(): void {
    this.used = 0;
  }

  take(): HTMLElement {
    let n = this.nodes[this.used];
    if (n === undefined) {
      n = this.root.ownerDocument.createElement('div');
      n.className = `gm-fx ${this.cls}`;
      n.setAttribute('aria-hidden', 'true');
      this.root.appendChild(n);
      this.nodes.push(n);
    }
    this.used += 1;
    if (n.style.display !== 'block') n.style.display = 'block';
    return n;
  }

  end(): void {
    for (let i = this.used; i < this.nodes.length; i++) {
      const n = this.nodes[i] as HTMLElement;
      if (n.style.display !== 'none') n.style.display = 'none';
    }
  }

  get shown(): number {
    return this.used;
  }
}

export function createDomFx(o: DomFxOptions): DomFx {
  const j = o.juice;
  const ev = j.events;
  const cfg: DomFxCfg = {
    coinCap: j.caps.coinFlights,
    floaterCap: j.caps.floaters,
    floaterAggMs: j.aggregation.floaterMs,
    coinBagMs: j.aggregation.coinBagMs,
    ribbonCap: j.caps.ribbons,
    ribbonRowEm: ev.discover.ribbonRowEm,
    ribbonNearRows: ev.discover.ribbonNearRows,
    sell: ev.sell,
  };
  // seeded: the same bursts fly the same arcs on every run (freezeAt screenshots)
  const model = new DomFxModel(cfg, o.rand ?? mulberry32(DOM_FX_SEED));
  const doc = o.root.ownerDocument;
  if (!o.instant) installStyle(doc, o.colors);
  const coins = new NodePool(o.root, 'gm-fx-coin');
  const floats = new NodePool(o.root, 'gm-fx-float');
  const ribbons = new NodePool(o.root, 'gm-fx-ribbon');
  /** Last known cell of every building (a removed one keeps its last cell for the refund coins). */
  const cells = new Map<number, { x: number; y: number; type: string; level: number }>();
  let loop = 0;
  let coinCss: string | null | undefined;
  const clock = new FxClock(o.maxStepMs);

  // ---- positions (client CSS px) ----
  const cellPt = (x: number, y: number): Pt | null => {
    const l = o.layout();
    return l ? cellCenter(l, x, y) : null;
  };
  const center = (r: {
    x: number;
    y: number;
    width?: number;
    w?: number;
    height?: number;
    h?: number;
  }): Pt => ({
    x: r.x + (r.width ?? r.w ?? 0) / 2,
    y: r.y + (r.height ?? r.h ?? 0) / 2,
  });
  /** A registered target with no area (hidden / not laid out) counts as missing: the layout stands in. */
  const target = (id: Parameters<typeof fxTarget>[0]): DOMRect | null => {
    const r = fxTarget(id);
    return r !== null && r.width > 0 && r.height > 0 ? r : null;
  };
  const hud = (): Pt | null => {
    const t = target('hudCash');
    if (t) return center(t);
    const l = o.layout();
    // fallback until the HUD registers: the HUD band's top-left quarter (where the cash sits)
    return l ? { x: l.hud.x + l.hud.w / 4, y: l.hud.y + l.hud.h / 2 } : null;
  };
  const counter = (): Pt | null => {
    const t = target('counter');
    if (t) return center(t);
    const l = o.layout();
    return l ? center(l.counter) : null;
  };
  /** Above a Gallery's rail (where its targets pop). */
  const railPt = (x: number, y: number): Pt | null => {
    const l = o.layout();
    const p = cellPt(x, y);
    if (!l || !p) return null;
    return { x: p.x, y: p.y + (GALLERY.railY - 0.5 - j.board.targetSize / 2) * l.cellCss };
  };

  const income60 = (): number => Math.max(0, o.incomePerSec()) * 60;

  // ---- drawing ----
  const coinBg = (px: number): string | null => {
    if (coinCss === undefined) {
      const url = o.coinImage(Math.max(px, ev.sell.bagPx));
      coinCss = url === null ? null : `url("${url}")`;
    }
    return coinCss;
  };

  const draw = (now: number): void => {
    coins.begin();
    for (const c of model.coins) {
      const p = coinAt(c, now);
      if (p === null) continue;
      const n = coins.take();
      const bg = coinBg(c.px);
      if (bg !== null) {
        if (n.style.backgroundImage !== bg) n.style.backgroundImage = bg;
      } else if (!n.classList.contains('css')) n.classList.add('css');
      n.style.width = `${c.px}px`;
      n.style.height = `${c.px}px`;
      n.style.transform = `translate3d(${p.x - c.px / 2}px,${p.y - c.px / 2}px,0)`;
      n.style.opacity = '1';
    }
    coins.end();
    floats.begin();
    for (const f of model.floaters) {
      const t = Math.max(0, Math.min(1, (now - f.start) / f.ms));
      const n = floats.take();
      const text = o.fill(o.copy.floater, { value: o.money(f.value) });
      if (n.textContent !== text) n.textContent = text;
      n.style.fontSize = `${f.px}px`;
      const rise = f.rise * (1 - (1 - t) * (1 - t));
      n.style.transform = `translate3d(${f.x}px,${f.y - rise}px,0) translate(-50%,-100%)`;
      const fade = ev.sell.floaterFadeFrom;
      n.style.opacity = String(t < fade ? 1 : Math.max(0, 1 - (t - fade) / (1 - fade)));
    }
    floats.end();
    ribbons.begin();
    for (const r of model.ribbons) {
      const t = Math.max(0, Math.min(1, (now - r.start) / r.ms));
      const n = ribbons.take();
      if (n.dataset.key !== String(r.key)) {
        n.dataset.key = String(r.key);
        n.className = `gm-fx gm-fx-ribbon ${r.kind}`;
        n.textContent = '';
        if (r.kind === 'new') {
          const b = doc.createElement('b');
          b.textContent = o.copy.newTag;
          n.appendChild(b);
          n.appendChild(doc.createTextNode(r.text));
        } else n.textContent = r.text;
        n.style.display = 'block';
      }
      n.style.fontSize = `${r.px}px`;
      const rise = r.rise * (1 - (1 - t) * (1 - t));
      n.style.transform = `translate3d(${r.x}px,${r.y - rise}px,0) translate(-50%,-100%)`;
      const fin = ev.discover.ribbonFadeIn;
      const fout = ev.discover.ribbonFadeOut;
      n.style.opacity = String(t < fin ? t / fin : t > 1 - fout ? Math.max(0, (1 - t) / fout) : 1);
    }
    ribbons.end();
  };

  const tick = (): void => {
    loop = 0;
    const now = clock.advance(o.now());
    const landed = model.step(now);
    if (landed > 0) {
      const win = doc.defaultView;
      win?.dispatchEvent(
        new CustomEvent('gm:fx-arrive', { detail: { target: 'hudCash', n: landed } }),
      );
    }
    draw(now);
    if (model.busy() || clock.freezing) loop = o.raf(tick);
  };
  const kick = (): void => {
    if (loop === 0 && model.busy()) loop = o.raf(tick);
  };
  /** The FX time of an event batch (a sleeping loop re-bases its clock first: no catch-up jump). */
  const nowForEvents = (): number => {
    if (loop === 0) clock.resync(o.now());
    return clock.now;
  };

  // ---- events ----
  const saleFx = (value: number, from: Pt, src: string, now: number, rm: boolean): void => {
    const to = hud();
    const i60 = income60();
    if (!rm && to !== null)
      model.coinBurst(coinCount(value, i60, ev.sell), from, to, now, {
        ms: ev.sell.coinFlightMs,
        toHud: true,
      });
    model.floater(src, value, from, now, floaterPx(value, i60, ev.sell));
  };

  const purchaseFx = (bid: number, now: number, rm: boolean): void => {
    if (rm) return; // RM: the HUD ticks
    const c = cells.get(bid) ?? null;
    const at = c ? cellPt(c.x, c.y) : null;
    const from = hud();
    if (at === null || from === null) return;
    model.coinBurst(ev.purchase.coins, from, at, now, { ms: ev.purchase.flightMs, toHud: false });
  };

  const refreshCells = (s: SimState): void => {
    for (const b of s.buildings) {
      if (b.type === 'chute') continue;
      const c = cells.get(b.id);
      if (c === undefined) cells.set(b.id, { x: b.x, y: b.y, type: b.type, level: b.level });
      else {
        c.x = b.x;
        c.y = b.y;
        c.level = b.level;
      }
    }
    if (cells.size > s.buildings.length * 2) {
      const live = new Set(s.buildings.map((b) => b.id));
      for (const id of [...cells.keys()]) if (!live.has(id)) cells.delete(id);
    }
  };

  /** A building's display name (a deposit: its ore's name) from its last known cell record. */
  const bldName = (bid: number): string => {
    const d = o.defs();
    const c = cells.get(bid);
    if (!c) return '';
    if (c.type === 'deposit') {
      const b = o.state().buildings.find((x) => x.id === bid);
      return b?.type === 'deposit' ? (d.deposits.find((x) => x.id === b.ore)?.name ?? '') : '';
    }
    return d.buildings[c.type as keyof SimDefs['buildings']]?.name ?? c.type;
  };

  /** A stat / name label floating above building `bid` (juice.json buildingMerge.labelMs, labelRisePx). */
  const label = (bid: number, text: string, now: number, rm: boolean): void => {
    const c = cells.get(bid);
    const p = c ? cellPt(c.x, c.y) : null;
    const l = o.layout();
    if (!p || !l || text === '') return;
    const bm = ev.buildingMerge;
    model.ribbon(
      'label',
      text,
      { x: p.x, y: p.y - l.cellCss * ev.discover.ribbonLiftCells },
      now,
      bm.labelMs,
      ev.discover.ribbonPx,
      rm ? 0 : bm.labelRisePx,
    );
  };

  const nameOf = (d: SimDefs, fam: string, tier: number): string =>
    d.families.find((f) => f.id === fam)?.names[tier - 1] ?? fam;

  return {
    onEvents(evs) {
      if (o.instant || evs.length === 0) return;
      const now = nowForEvents();
      const rm = o.reduceMotion();
      const s = o.state();
      const d = o.defs();
      // buildings placed by this batch: their cells for the purchase coins / labels
      refreshCells(s);
      // where a new tier appeared in this batch (the NEW! ribbon)
      const born = new Map<string, Pt>();
      for (const e of evs) {
        if (e.e === 'merge' || e.e === 'spawn') {
          const p = cellPt(e.x, e.y);
          if (p) born.set(`${e.fam}:${e.tier}`, p);
        } else if (e.e === 'craft') {
          const c = cells.get(e.bid);
          const p = c ? cellPt(c.x, c.y) : null;
          if (p) born.set(`${e.fam}:${e.tier}`, p);
        }
      }
      const l = o.layout();
      const cell = l?.cellCss ?? 0;
      for (const e of evs) {
        switch (e.e) {
          case 'sell': {
            if (e.value <= 0) break;
            const from = e.where === 'counter' ? counter() : cellPt(e.x, e.y);
            if (from)
              saleFx(
                e.value,
                from,
                e.where === 'counter' ? 'counter' : `cell:${e.x},${e.y}`,
                now,
                rm,
              );
            break;
          }
          case 'change':
          case 'bremove': {
            if (e.e === 'bremove') o.shots.forget(e.bid);
            const value = e.e === 'change' ? e.value : e.refund;
            const c = cells.get(e.bid);
            const from = c ? cellPt(c.x, c.y) : null;
            if (value > 0 && from) saleFx(value, from, `bld:${e.bid}`, now, rm);
            break;
          }
          case 'fire': {
            if (e.payout <= 0) break;
            const c = cells.get(e.bid);
            const at = c ? railPt(c.x, c.y) : null;
            if (at === null) break;
            const i60 = income60();
            const src = `gallery:${e.bid}`;
            // one decision per shot, shared with the board (≤ 4/s per Gallery, 8/s in all): a coin flies
            // only for a shot the board shows (decided even under reduce motion, where no coin flies)
            const visible = o.shots.visible(e, now);
            if (!rm && visible) {
              const to = hud();
              if (to)
                model.coinBurst(ev.galleryFire.coins, at, to, now, {
                  ms: ev.sell.coinFlightMs,
                  toHud: true,
                });
            }
            const px = floaterPx(e.payout, i60, ev.sell);
            // practice taps (the cold open) float one by one; automated fire sums per window
            if (e.practice) model.floater(`${src}:tap`, e.payout, at, now, px);
            else
              model.galleryShot(
                src,
                e.payout,
                at,
                now,
                px,
                rm ? j.reduceMotion.galleryFloaterMs : j.aggregation.galleryFloaterMs,
              );
            break;
          }
          case 'place':
            purchaseFx(e.bid, now, rm);
            break;
          case 'bmerge': {
            if (e.other === 0) purchaseFx(e.bid, now, rm);
            label(e.bid, o.fill(o.copy.level, { name: bldName(e.bid), level: e.level }), now, rm);
            break;
          }
          case 'built':
            // a first build completes: its name floats up (UX §5 BuildCompleted "name ribbon")
            label(e.bid, bldName(e.bid), now, rm);
            break;
          case 'discover': {
            const p = born.get(`${e.fam}:${e.tier}`);
            if (!p) break;
            const dsc = ev.discover;
            model.ribbon(
              'new',
              nameOf(d, e.fam, e.tier),
              { x: p.x, y: p.y - cell * dsc.ribbonLiftCells },
              now,
              dsc.ribbonMs,
              dsc.ribbonPx,
              rm ? 0 : dsc.ribbonRisePx,
            );
            break;
          }
          default:
            break;
        }
      }
      kick();
    },
    reset() {
      model.clear();
      o.shots.clear();
      cells.clear();
      if (loop !== 0) o.cancelRaf(loop);
      loop = 0;
      draw(clock.now);
    },
    stats() {
      return {
        ...model.stats(),
        nodes: coins.shown + floats.shown + ribbons.shown,
        clockMs: clock.now,
        frozen: clock.frozen,
      };
    },
    resetPeaks() {
      model.resetPeaks();
    },
    busy: () => model.busy(),
    freezeAt(ms) {
      if (loop === 0) clock.resync(o.now());
      clock.freezeAt(ms);
      if (loop === 0) loop = o.raf(tick);
    },
  };
}
